/**
 * iter110n · CEO 2026-09-22 · Inline contract-role dropdown for /embauche cards.
 *
 * Displays the currently attributed contract role (or a fallback like the
 * layan offer title / free-text position) directly under the candidate name
 * on the Kanban card. Clicking the small chevron opens a dropdown listing
 * ALL built-in + custom roles (loaded lazily on first open). Selecting a
 * new role hits `POST /api/candidates/{id}/contract-role`, shows a toast
 * and calls `onChange(newSlug, newLabel)` so the parent can update its
 * local state immediately (optimistic UX — no full pipeline reload).
 *
 * Read-only fallback : if the user is NOT admin AND NOT a coord with
 * `can_manage_contracts`, we render the static label (no chevron, no
 * click handler) so the pipeline card degrades gracefully.
 */
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";


export default function InlineContractRoleDropdown({
    candidateId,
    contractRoleSlug,       // current slug (nullable)
    contractRoleLabel,      // resolved label from the server (nullable)
    fallbackLabel,          // free-text position / layan_offer_title
    onChange,               // ({slug, label}) => void — optimistic update
    dataTestidPrefix = "role",
}) {
    const { user } = useAuth();
    const canEdit = !!(
        user?.role === "admin" ||
        (user?.additional_roles || []).includes("admin") ||
        (user?.role === "coordinateur" && user?.can_manage_contracts) ||
        ((user?.additional_roles || []).includes("coordinateur") && user?.can_manage_contracts)
    );

    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState(null);        // loaded on first open
    const [loadingOpts, setLoadingOpts] = useState(false);
    const wrapperRef = useRef(null);

    // Displayed label — prefer explicit contract_role_label from the API,
    // fall back to the free-text position column. If nothing at all, hide.
    const displayLabel = contractRoleLabel || fallbackLabel || "";

    // Close on outside click (defensive — dropdowns must always close).
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    // Lazy-load the role options the first time the user opens the dropdown.
    const loadOptions = useCallback(async () => {
        if (options !== null) return; // cached
        setLoadingOpts(true);
        try {
            const { data } = await api.get("/contracts/options/list");
            setOptions(data?.roles || []);
        } catch (e) {
            toast.error("Impossible de charger la liste des rôles");
            setOptions([]);
        } finally {
            setLoadingOpts(false);
        }
    }, [options]);

    const handleToggle = useCallback(
        (e) => {
            e.stopPropagation(); // don't open the card drawer
            if (!canEdit) return;
            const next = !open;
            setOpen(next);
            if (next) loadOptions();
        },
        [canEdit, open, loadOptions],
    );

    const handlePick = useCallback(
        async (roleKey, roleLabel) => {
            if (saving) return;
            if (roleKey === contractRoleSlug) {
                setOpen(false);
                return;
            }
            setSaving(true);
            try {
                await api.post(`/candidates/${candidateId}/contract-role`, {
                    role: roleKey,
                });
                toast.success(`Poste : ${roleLabel}`);
                onChange?.({ slug: roleKey, label: roleLabel });
                setOpen(false);
            } catch (e) {
                const msg = e?.response?.data?.detail || "Échec de la mise à jour";
                toast.error(msg);
            } finally {
                setSaving(false);
            }
        },
        [candidateId, contractRoleSlug, onChange, saving],
    );

    if (!displayLabel && !canEdit) return null;

    // Static (read-only) render — pre-rec / referent see the label, no chevron.
    if (!canEdit) {
        return (
            <div
                className="text-[11px] text-slate-500 break-words mt-0.5"
                data-testid={`${dataTestidPrefix}-static-${candidateId}`}
            >
                {displayLabel}
            </div>
        );
    }

    return (
        <div
            ref={wrapperRef}
            className="relative mt-0.5"
            onClick={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                onClick={handleToggle}
                disabled={saving}
                data-testid={`${dataTestidPrefix}-trigger-${candidateId}`}
                title="Changer le poste attribué"
                className={`inline-flex items-center gap-1 max-w-full text-[11px] font-medium rounded-md px-1.5 py-0.5 border transition ${
                    contractRoleSlug
                        ? "bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                } ${saving ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
            >
                <span className="truncate">
                    {displayLabel || "Attribuer un poste…"}
                </span>
                {saving ? (
                    <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                ) : (
                    <ChevronDown
                        className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                )}
            </button>

            {open && (
                <div
                    className="absolute z-30 mt-1 left-0 min-w-[220px] max-w-[300px] rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden"
                    data-testid={`${dataTestidPrefix}-menu-${candidateId}`}
                >
                    {loadingOpts && (
                        <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Chargement…
                        </div>
                    )}
                    {!loadingOpts && options && options.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                            Aucun rôle disponible
                        </div>
                    )}
                    {!loadingOpts && options && options.length > 0 && (
                        <ul className="max-h-72 overflow-y-auto py-1">
                            {options.map((opt) => {
                                const active = opt.key === contractRoleSlug;
                                return (
                                    <li key={opt.key}>
                                        <button
                                            type="button"
                                            onClick={() => handlePick(opt.key, opt.label_long || opt.label)}
                                            disabled={saving}
                                            data-testid={`${dataTestidPrefix}-option-${opt.key}-${candidateId}`}
                                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-2 transition ${
                                                active
                                                    ? "bg-orange-50 text-orange-900 font-semibold"
                                                    : "text-slate-700 hover:bg-slate-50"
                                            }`}
                                        >
                                            <span className="truncate">
                                                {opt.label_long || opt.label}
                                            </span>
                                            {active && (
                                                <Check className="w-3.5 h-3.5 shrink-0 text-orange-600" />
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                            {/* Clear-attribution option — only visible when a role is currently set. */}
                            {contractRoleSlug && (
                                <li className="border-t border-slate-100">
                                    <button
                                        type="button"
                                        onClick={() => handlePick(null, "")}
                                        disabled={saving}
                                        data-testid={`${dataTestidPrefix}-option-clear-${candidateId}`}
                                        className="w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 italic"
                                    >
                                        Retirer l'attribution
                                    </button>
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
